/**
 * BaseDocumentLayout — the single, uniform shell for every KrewUp document
 * rendered in the browser (screen + print / Export to PDF).
 *
 * It owns the common chrome: company header (logo + details), document title
 * block, consistent typography/spacing, and footer. Each document type's
 * renderer supplies ONLY its body via `children`. This guarantees every PDF
 * looks like the same product regardless of type.
 *
 * Print behaviour (page margins, hiding app chrome) is handled by the viewer's
 * print stylesheet; this component stays presentational and print-safe (no
 * fixed heights, flow layout, web-safe fonts).
 */
import type { ReactNode } from 'react';

export interface DocumentCompany {
  name?: string | null;
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  logoUrl?: string | null;
}

export interface BaseDocumentLayoutProps {
  company?: DocumentCompany | null;
  /** Uppercase document label, e.g. "PURCHASE ORDER". */
  title: string;
  /** Human reference, e.g. "#PO000123". */
  reference?: string | null;
  /** Right-aligned metadata rows (status, dates, …). */
  meta?: Array<{ label: string; value: string | null | undefined }>;
  children: ReactNode;
}

const INK = 'rgb(var(--text-color-primary))';
const MUTED = 'rgb(var(--text-color-muted))';

export function BaseDocumentLayout({ company, title, reference, meta = [], children }: BaseDocumentLayoutProps) {
  const metaRows = meta.filter((m) => m.value != null && m.value !== '');

  return (
    <div style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif", color: INK, fontSize: 13, lineHeight: 1.5 }}>
      {/* Header: company (left) + document title (right) */}
      <div
        style={{
          display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 16,
          alignItems: 'flex-start', borderBottom: `2px solid ${INK}`, paddingBottom: 16, marginBottom: 20,
        }}
      >
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
          {company?.logoUrl ? (
            <img src={company.logoUrl} alt="" style={{ maxWidth: 160, maxHeight: 56, objectFit: 'contain' }} />
          ) : null}
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>{company?.name || 'Company'}</div>
            {company?.address && <div style={{ color: MUTED }}>{company.address}</div>}
            {company?.phone && <div style={{ color: MUTED }}>{company.phone}</div>}
            {company?.email && <div style={{ color: MUTED }}>{company.email}</div>}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 0.5 }}>{title}</div>
          {reference && <div style={{ marginTop: 4, fontWeight: 600 }}>{reference}</div>}
          {metaRows.map((m) => (
            <div key={m.label} style={{ color: MUTED }}>
              {m.label}: {m.value}
            </div>
          ))}
        </div>
      </div>

      {/* Body (document-specific) */}
      <div>{children}</div>

      {/* Footer */}
      <div style={{ marginTop: 32, paddingTop: 12, borderTop: `1px solid rgb(var(--gray-200))`, color: 'rgb(var(--gray-400))', fontSize: 11, textAlign: 'center' }}>
        Generated with KrewUp
      </div>
    </div>
  );
}
